import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { AXIOS_RESERVATION } from "../utils/axios-interceptors";

export interface IHotelConfig {
  hotelId: string;
  hotelCode: string;
  hotelName: string;
  hotelLogo: string;
  hotelLogoBlack: string;
}

const getStorageKey = (hotelcode: string) => `hotel-config-${hotelcode}`;

const readFromStorage = (hotelcode: string): IHotelConfig | undefined => {
  try {
    const raw = localStorage.getItem(getStorageKey(hotelcode));
    return raw ? JSON.parse(raw) : undefined;
  } catch {
    return undefined;
  }
};

const useGetHotelConfig = () => {
  const { hotelcode } = useParams<{ hotelcode: string }>();

  const query = useQuery<IHotelConfig>({
    queryKey: ["hotel-config", hotelcode],
    queryFn: async () => {
      const response = await AXIOS_RESERVATION.get(`v1/hotels/${hotelcode}`);
      return response.data.result.data;
    },
    enabled: !!hotelcode,
    staleTime: 5 * 60 * 1000,
    initialData: hotelcode ? readFromStorage(hotelcode) : undefined,
  });

  useEffect(() => {
    if (query.data && hotelcode) {
      console.log("Storing hotel config in localStorage:", query.data);
      localStorage.setItem(getStorageKey(hotelcode), JSON.stringify(query.data));
      if (query.data.hotelName) {
        document.title = `${query.data.hotelName} Admin`;
      }
      if (query.data.hotelLogo) {
        const link =
          (document.querySelector("link[rel='icon']") as HTMLLinkElement) ||
          (() => {
            const el = document.createElement("link");
            el.rel = "icon";
            document.head.appendChild(el);
            return el;
          })();
        link.href = query.data.hotelLogo;
      }
    }
  }, [query.data, hotelcode]);

  return query;
};

export default useGetHotelConfig;
